/**
 * Domain Enums - Shared enumeration types for the Admissions domain
 *
 * Per GRPO: All magic values defined here, never inline
 */
import { z } from "zod";
import {
  GradeLevel,
  Priority,
  SchoolDivision,
} from "@triedge/domain-core/types";

export { GradeLevel, Priority, SchoolDivision };

// ============================================================================
// Application Status Workflow
// ============================================================================

export const ApplicationStatus = z.enum([
  "draft", // Parent started, not submitted
  "submitted", // Parent completed and submitted
  "under_review", // Staff reviewing
  "interview_scheduled", // Interview date set
  "decision_pending", // Post-interview, awaiting decision
  "accepted", // Approved (initial acceptance)
  "accepted_pending_enrollment", // Accepted, enrollment checklist in progress
  "acceptance_expired", // Family did not complete enrollment by deadline
  "waitlisted", // Pending availability
  "denied", // Not approved
  "enrolled", // Fully enrolled for academic year
  "withdrawn", // Family withdrew application
]);
export type ApplicationStatus = z.infer<typeof ApplicationStatus>;

// ============================================================================
// Student Gender
// ============================================================================

export const StudentGender = z.enum(["male", "female", "other"]);
export type StudentGender = z.infer<typeof StudentGender>;

// ============================================================================
// Contact Types
// ============================================================================

export const ContactType = z.enum(["email", "phone", "in_person", "sms"]);
export type ContactType = z.infer<typeof ContactType>;

export const ContactDirection = z.enum(["inbound", "outbound"]);
export type ContactDirection = z.infer<typeof ContactDirection>;

// ============================================================================
// Note Types
// ============================================================================

export const NoteType = z.enum(["general", "health", "interview", "follow_up"]);
export type NoteType = z.infer<typeof NoteType>;

// ============================================================================
// User Roles
// ============================================================================

export const UserRole = z.enum([
  "staff",
  "nurse",
  "admin",
  "owner",
  "editor",
  "viewer",
  "parent", // v1.2: Parent/Guardian access for enrollment portal
]);
export type UserRole = z.infer<typeof UserRole>;

// ============================================================================
// Dashboard View Preferences
// ============================================================================

export const DashboardView = z.enum(["list", "kanban", "calendar", "insights"]);
export type DashboardView = z.infer<typeof DashboardView>;

// ============================================================================
// Parent Relation
// ============================================================================

export const ParentRelation = z.enum(["father", "mother", "guardian"]);
export type ParentRelation = z.infer<typeof ParentRelation>;

// ============================================================================
// Immunization Status (v1.2: Enrollment Portal)
// ============================================================================

export const ImmunizationStatus = z.enum([
  "up_to_date",
  "partial",
  "exempt", // Note: YST does not accept religious exemptions
  "not_provided",
]);
export type ImmunizationStatus = z.infer<typeof ImmunizationStatus>;

// ============================================================================
// Document Categories
// ============================================================================

export const DocumentCategory = z.enum([
  "transcript",
  "health_form",
  "recommendation",
  "birth_certificate",
  "immunization",
  "tuition_contract",
  "tuition_contract_signed",
  "other",
]);
export type DocumentCategory = z.infer<typeof DocumentCategory>;
